import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { FRONTEND_BUILD } from '../version';

// Build-freshness badge for QA. Shows:
//  - the frontend bundle's git SHA (compile-time)
//  - the live backend deploy SHA (GET /api/version)
//  - on a NATIVE device: the installed app's version + build number
//    (Capacitor App.getInfo()) — so QA can tell a STALE INSTALLED APP apart
//    from stale web, the exact confusion behind the login/biometric reports.
//
// Force it open on any page with ?debug=build.
const BuildBadge = () => {
  const forced = (() => {
    try { return new URLSearchParams(window.location.search).get('debug') === 'build'; }
    catch { return false; }
  })();
  const [api, setApi] = useState(null);
  const [native, setNative] = useState(null);
  const [open, setOpen] = useState(forced);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (base) {
      fetch(`${base}/api/version`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => setApi(d))
        .catch(() => {});
    }
    let isNative = false;
    try { isNative = !!Capacitor?.isNativePlatform?.(); } catch { isNative = false; }
    if (isNative) {
      import('@capacitor/app')
        .then((m) => m.App.getInfo())
        .then((info) => setNative({ version: info?.version, build: info?.build }))
        .catch(() => {});
    }
  }, []);

  const feSha = FRONTEND_BUILD.sha;
  const beSha = api?.sha;
  const matched = beSha && feSha && beSha === feSha;
  const known = feSha && feSha !== 'dev';
  const stale = beSha && known && !matched;

  const color = stale ? '#B45309' : matched ? '#3D9970' : '#78716C';
  const bg = stale ? '#FEF3C7' : matched ? '#ECFDF5' : '#F5F3EF';

  const rel = (iso) => {
    if (!iso) return '';
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 90) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  };

  const platform = (() => { try { return Capacitor?.getPlatform?.() || 'web'; } catch { return 'web'; } })();

  return (
    <div
      data-testid="build-badge"
      onClick={() => setOpen((v) => !v)}
      title="Build version — tap for details"
      style={{
        position: 'fixed', left: 8, bottom: 8, zIndex: 9998,
        cursor: 'pointer', fontFamily: 'ui-monospace, monospace',
        fontSize: 10, lineHeight: 1.35, color, background: bg,
        border: `1px solid ${color}33`, borderRadius: 8,
        padding: open ? '8px 10px' : '3px 7px', opacity: open ? 0.98 : 0.55,
        boxShadow: open ? '0 6px 20px rgba(0,0,0,0.15)' : 'none',
        maxWidth: open ? 280 : 'unset', backdropFilter: 'blur(4px)',
        transition: 'opacity .15s, padding .15s',
      }}
    >
      {!open ? (
        <span data-testid="build-badge-sha">
          {stale ? '⚠ ' : matched ? '● ' : ''}fe:{feSha}{beSha ? ` · api:${beSha}` : ''}
          {native?.version ? ` · app:${native.version}` : ''}
        </span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <b style={{ color }}>{stale ? '⚠ STALE WEB DEPLOY' : matched ? '● Web build matched' : 'Build info'}</b>
          <span>platform: <b>{platform}</b></span>
          <span>frontend: <b>{feSha}</b>{FRONTEND_BUILD.time ? ` · ${rel(FRONTEND_BUILD.time)}` : ''}</span>
          <span>backend&nbsp;: <b>{beSha || '—'}</b>{api?.commit_time ? ` · ${rel(api.commit_time)}` : ''}</span>
          {native ? (
            <span data-testid="build-badge-native">app bin&nbsp;: <b>v{native.version || '?'}</b> (build {native.build || '?'})</span>
          ) : (
            platform !== 'web' && <span>app bin&nbsp;: <b>loading…</b></span>
          )}
          {api?.started_at && <span>api up: {rel(api.started_at)}</span>}
          {stale && <span style={{ color }}>frontend & backend SHAs differ — redeploy the stale side.</span>}
          {native && <span style={{ color: '#78716C' }}>If the app bin is old but web is current, reinstall the latest native build.</span>}
        </div>
      )}
    </div>
  );
};

export default BuildBadge;
